/**
 * THE TRADE DECORATOR: a click target on the trade line, and the card it toggles.
 *
 * <h2>Why the line is drawn as TWO segments rather than one</h2>
 *
 * The requirement is that clicking the first half of a trade line toggles the ENTRY card and the
 * second half toggles the EXIT card. There are two ways to know which half was clicked, and they
 * are not close:
 *
 * <ol>
 *   <li><b>Two segments</b> — split the connector at its chronological midpoint and give each half
 *       its own datum. ECharts hands the clicked datum back in {@code params.data}, so the half is
 *       READ, never computed.</li>
 *   <li><b>One segment plus pixel maths</b> — take {@code event.offsetX} and work out where along
 *       the line it fell.</li>
 * </ol>
 *
 * The second is wrong three times over, and each is the kind of wrong that looks fine in a
 * screenshot:
 *
 * <ul>
 *   <li><b>offsetX is not position along the line.</b> A trade line is a diagonal. Comparing an x
 *       pixel against the midpoint's x answers "which half of the TIME span" — which happens to be
 *       the question here, but only because the split is chronological. The moment anyone wants the
 *       geometric midpoint it needs projection onto the line, and the x-only version would be
 *       silently off on every steep trade.</li>
 *   <li><b>It must be redone on every zoom and pan.</b> The midpoint is fixed in data space and
 *       moves in pixel space, so the maths needs the live axis projection — {@code convertFromPixel}
 *       and therefore the chart instance. Two segments are hit-tested by ECharts in whatever
 *       projection is current, for free and always correctly.</li>
 *   <li><b>It re-derives hit-testing that already happened.</b> To know the click was on the line
 *       at all, zrender has already solved point-to-polyline distance with its own tolerance. Doing
 *       it again in application code means a second, differently-tolerant answer to the same
 *       question.</li>
 * </ul>
 *
 * <p>The cost of two segments is that they must look like one line: identical pen, and an EXACTLY
 * shared endpoint. {@link splitConnector} computes the midpoint ONCE and hands the same values to
 * both halves, so there is no floating-point hairline between them.
 */

/** Which decorator a clicked half toggles. */
export const TRADE_HALF = {
  ENTRY: 'entry',
  EXIT: 'exit',
  /** Both — a trade too short to have two halves. See {@link splitConnector}. */
  BOTH: 'both',
};

/**
 * The shortest span, in bars, that can carry two distinct clickable halves.
 *
 * <p>On a CATEGORY axis a segment's endpoints are bar indices, so a one-bar trade has nowhere to
 * put a midpoint: whichever way the rounding goes, one half collapses to zero length and becomes
 * unclickable. Rather than ship a half nobody can reach, a short trade is drawn as a single segment
 * that toggles both cards — see {@link TRADE_HALF.BOTH}.
 */
export const MIN_SPLITTABLE_SPAN = 2;

/**
 * THE CLICK TARGET'S WIDTH, in pixels, for the invisible companion line.
 *
 * <p>The drawn pen is 1.5px and a trade is SHORT: on SPY's full history a six-day trade is one
 * pixel wide, and its stroke is not something a human can hit. Measured at full zoom, every
 * segment's bounding rect is 0x0. So the visible line stays thin and silent, and a transparent
 * stroke of this width with ROUND CAPS carries the clicks -- the cap is what turns a one-pixel
 * segment into a disc, which is the only reason a short trade is reachable at all.
 *
 * <p>Seven pixels is about one candle at mid zoom. It is a real cost -- a click this close to a
 * trade line no longer reaches the candle underneath -- and the alternative to a modest hit area
 * is not a smaller one, it is a feature nobody can use.
 */
export const TRADE_HIT_WIDTH = 7;

/** A stable identity for one decorator, used as the key of the visible-set. */
export const decoratorKey = (tradeKey, half) => `${tradeKey}:${half}`;

/**
 * THE CARDS ONE CLICKED SEGMENT TOGGLES.
 *
 * <p>Two for a BOTH segment, one otherwise. A short trade is drawn as a single segment because it
 * has nowhere to put a midpoint, and there is no combined card any more — so its one segment has
 * to reach the two cards the trade does have.
 *
 * <p>Here rather than in the click handler so the chart and anything else that opens a card agree
 * about what a BOTH segment means. A handler that special-cased it locally is how `t1:both` came
 * to be a key that addressed a card nobody could otherwise reach.
 */
export const cardKeysFor = (tradeKey, half) =>
  (half === TRADE_HALF.BOTH
    ? [decoratorKey(tradeKey, TRADE_HALF.ENTRY), decoratorKey(tradeKey, TRADE_HALF.EXIT)]
    : [decoratorKey(tradeKey, half)]);

/**
 * Splits one connector into its clickable halves.
 *
 * @param connector {{date1,price1,date2,price2,tradeKey,...}} as the trade builders emit it
 * @param indexOf   a Map from date string to bar index — built once by the caller, because this
 *                  runs per trade and an indexOf scan per call is the 36-million-comparison
 *                  mistake the sibling module already made once
 * @returns an array of 1 or 2 segments, each carrying `half` and the original connector's pen
 */
export const splitConnector = (connector, indexOf) => {
  if (!connector) return [];
  const i1 = indexOf.get(connector.date1);
  const i2 = indexOf.get(connector.date2);
  const pen = {
    color: connector.color,
    width: connector.width,
    lineType: connector.lineType,
    opacity: connector.opacity,
    tradeKey: connector.tradeKey,
  };

  // Either endpoint off the chart, or a span too short to halve: one segment, both cards.
  if (i1 === undefined || i2 === undefined || Math.abs(i2 - i1) < MIN_SPLITTABLE_SPAN) {
    return [{
      ...pen,
      half: TRADE_HALF.BOTH,
      coords: [[connector.date1, connector.price1], [connector.date2, connector.price2]],
    }];
  }

  // THE MIDPOINT, COMPUTED ONCE. Both halves are handed the identical date and price, so the two
  // segments meet exactly and render as one unbroken line. Computing it twice -- once per half --
  // would be the same arithmetic and is exactly how a hairline gap appears at some zoom levels.
  const midIndex = Math.round((i1 + i2) / 2);
  const midDate = indexOf.dates[midIndex];
  // Linear interpolation in PRICE at the midpoint bar. The line is straight, so this is the point
  // the line already passes through -- the split changes where the click is attributed, never
  // where the line is drawn.
  const t = (midIndex - i1) / (i2 - i1);
  const midPrice = connector.price1 + (connector.price2 - connector.price1) * t;

  return [
    {
      ...pen,
      half: TRADE_HALF.ENTRY,
      coords: [[connector.date1, connector.price1], [midDate, midPrice]],
    },
    {
      ...pen,
      half: TRADE_HALF.EXIT,
      coords: [[midDate, midPrice], [connector.date2, connector.price2]],
    },
  ];
};

/** A date→index lookup that also carries the array, for the midpoint date. */
export const dateIndex = (dates) => {
  const map = new Map();
  const list = Array.isArray(dates) ? dates : [];
  for (let i = 0; i < list.length; i++) map.set(list[i], i);
  map.dates = list;
  return map;
};

/**
 * EVERY TRADE CARD the given overlays can show, as decorator keys.
 *
 * <p>DERIVED from the overlays rather than tracked beside them, so "all" always means what is
 * actually drawn right now. A separately maintained list goes stale the moment a run is repeated
 * with one trade more, and the symptom is keys that open nothing and cards that cannot be closed.
 *
 * <p>Only the halves a connector actually HAS, which is now exactly ENTRY and EXIT. There is no
 * `both` key: a trade too short to split is one clickable SEGMENT that opens the two cards, not
 * a third card carrying both. Before that, every trade emitted three cards and the combined one
 * was drawn directly over the entry card at the same anchor -- 774 cards for 258 trades, a third
 * of them redundant and each one an overlap.
 *
 * <p>Shared by the chart (which toggles them) and by App (which counts them for the Admin Panel's
 * label). Two counts of the same thing is how a button comes to say "Show all (32)" and then show
 * thirty-one.
 */
export const tradeCardKeys = (overlays) => {
  const keys = [];
  (Array.isArray(overlays) ? overlays : []).forEach((o) => {
    (o && Array.isArray(o.connectors) ? o.connectors : []).forEach((c) => {
      if (!c || !c.tradeKey || !c.decorators) return;
      Object.keys(c.decorators).forEach((half) => keys.push(decoratorKey(c.tradeKey, half)));
    });
  });
  return keys;
};

/* ── the card's content ───────────────────────────────────────────────────────────────────── */

/**
 * The three standardized lines: Action, Date, Price.
 *
 * <p>The ACTION carries a qualifier in brackets, which is the single most useful fact about that
 * end of the trade: for an entry its direction, for an exit the rule that ended it. An intraday
 * fill is named as well, because an intraday exit lands on the same daily candle an end-of-day exit
 * would and the card is the only place the difference can be stated.
 *
 * <p>Price is formatted to four decimals and NOT rounded to two. These are fills: a stop that
 * transacted at 43.6937 is not 43.69, and a reader checking the card against the ledger needs the
 * number the ledger holds.
 */
export const decoratorLines = (trade, half) => {
  if (!trade) return [];
  const entry = half === TRADE_HALF.EXIT ? false : true;
  const lines = [];

  if (half === TRADE_HALF.BOTH) {
    // A trade too short to halve shows both ends, because its two prices are the whole of what
    // there is to say about it and they are usually different.
    return [
      ...decoratorLines(trade, TRADE_HALF.ENTRY),
      '',
      ...decoratorLines(trade, TRADE_HALF.EXIT),
    ];
  }

  lines.push(entry ? `ENTRY (${entryQualifier(trade)})` : `EXIT (${exitQualifier(trade)})`);
  lines.push(sessionDate(entry ? trade.entryTime : trade.exitTime) || '—');
  lines.push(priceText(entry ? trade.entryPrice : trade.exitPrice));
  return lines;
};

const entryQualifier = (trade) => {
  const direction = (trade.direction || '').toUpperCase() === 'SHORT' ? 'SHORT' : 'LONG';
  const tf = timeframeQualifier(trade.entryTimeframe);
  return tf ? `${direction} · ${tf}` : direction;
};

const exitQualifier = (trade) => {
  const reason = trade.exitReason || trade.exitReasonCode || 'CLOSED';
  const tf = timeframeQualifier(trade.exitTimeframe);
  const text = String(reason).toUpperCase();
  return tf ? `${text} · ${tf}` : text;
};

/** Named only when it is INTRADAY — "daily" is the unremarkable case and says nothing. */
const timeframeQualifier = (timeframe) => {
  const tf = (timeframe || '').toLowerCase();
  if (!tf || tf === 'daily') return '';
  return tf.toUpperCase();
};

/** The session date. A STRING SLICE, never through Date -- see the note in annualBreakdown. */
export const sessionDate = (iso) =>
  (typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : null);

const priceText = (price) =>
  (Number.isFinite(price) ? Number(price).toFixed(4) : '—');

/**
 * THE PNL, FORMATTED AND SIGNED.
 *
 * <p>Always carries its sign, including the plus. A column of percentages where only the losses
 * are marked reads as "2.45" being neutral, and these are compared against each other more often
 * than they are read alone.
 *
 * <p>Two decimals, unlike the price. A price is a FILL and must match the ledger to the tick; a
 * return is a measurement, and the fourth decimal of a percentage is noise that costs width in a
 * tag whose whole point is to be small.
 */
export const pnlText = (pnl) =>
  (Number.isFinite(pnl) ? `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}%` : '—');

/** Which accent a return takes. Zero is NEITHER: a scratch is not a win. */
export const pnlTone = (pnl) => {
  if (!Number.isFinite(pnl) || pnl === 0) return 'flat';
  return pnl > 0 ? 'win' : 'loss';
};

/**
 * THE CARD AS RICH TEXT, one style per line.
 *
 * <p>Every line is wrapped in its own rich tag rather than left as bare text. Bare text inside a
 * rich label takes the base style, which works — but then the action, the date and the price
 * cannot be weighted differently, and the difference between a heading and its data is the whole
 * reason a dense tag stays readable at ten pixels.
 *
 * <p>Wrapping also removes an ambiguity: once `rich` is defined ECharts parses the WHOLE string
 * for <code>{style|text}</code>, so an exit reason that ever contained a brace would be read as
 * markup. With every line tagged there is no bare text left to misparse.
 *
 * <h3>THE PNL IS ON THE EXIT CARD ONLY</h3>
 *
 * It was on both for a while, on the reasoning that both cards describe one trade and a reader
 * should not have to open the exit to learn whether the entry was any good. That is true and it
 * is outweighed: a return printed beside an ENTRY reads as something known at the entry, which is
 * the one thing it is not. A result belongs on the card that reports the result.
 *
 * <p>It also costs a line and a colour on every entry card, and the two cards now sit on opposite
 * sides of the price action — so the pair are rarely read together anyway.
 *
 * <p>There is NO combined card. A trade too short to split is one clickable segment that opens
 * BOTH cards, rather than one card containing both — see `TRADE_HALF.BOTH`.
 */
export const decoratorRichText = (trade, half) => {
  if (!trade) return '';
  const [action, date, price] = decoratorLines(trade, half);
  const lines = [
    // The ACTION is the only line that can exceed the cap, because it carries the exit rule.
    // Wrapped HERE rather than by ECharts -- see DECORATOR_WIDTH for why neither `width`
    // placement does what it looks like it does.
    ...wrapToChars(action).map((l) => `{a|${l}}`),
    `{d|${date}}`,
    `{p|${price}}`,
  ];
  if (half === TRADE_HALF.EXIT) {
    lines.push(`{${pnlTone(trade.pnlPercent)}|${pnlText(trade.pnlPercent)}}`);
  }
  return lines.join('\n');
};

/* ── directional hemisphere anchoring ─────────────────────────────────────────────────────── */

/** Which side of the price action a card sits on. */
export const CARD_SIDE = { ABOVE: 'above', BELOW: 'below' };

/**
 * THE HEMISPHERE A CARD BELONGS TO, from the trade's direction and which end this card is.
 *
 * <pre>
 *            ENTRY    EXIT
 *   LONG     below    above
 *   SHORT    above    below
 * </pre>
 *
 * <p>Read it as the card sitting on the side price had to TRAVEL TOWARDS for the trade to work.
 * A long is opened at the bottom of its intended move and closed at the top, so its entry card
 * hangs beneath the bar and its exit card floats above — and the two cards of a winning long
 * therefore straddle the price action in the direction the trade actually went. A short is the
 * mirror. Direction is legible from the LAYOUT before a word is read.
 *
 * <p>It also halves the local density for free, which is the collision half of the brief: entry
 * and exit cards can no longer contend for the same vertical space, because they are never on the
 * same side of the bar.
 *
 * <p>Anything that is not explicitly SHORT is treated as long, matching `entryQualifier` — one
 * reading of a missing direction, not two.
 */
export const cardSide = (trade, half) => {
  const short = (trade?.direction || '').toUpperCase() === 'SHORT';
  // BOTH is anchored at the entry and opens with the entry line, so it takes the entry's side.
  const isExit = half === TRADE_HALF.EXIT;
  const below = short ? isExit : !isExit;
  return below ? CARD_SIDE.BELOW : CARD_SIDE.ABOVE;
};

/** The ECharts label position that puts the box on that side of its anchor. */
export const labelPositionFor = (side) => (side === CARD_SIDE.BELOW ? 'bottom' : 'top');

/**
 * THE PRICE THE CARD ANCHORS TO — the bar's own extreme, not the fill.
 *
 * <p>The brief asks for a card strictly below the LOW or above the HIGH, and positioning a label
 * `bottom` of the FILL does not achieve that: a long entered on its bar's high would hang its
 * card across the candle body it is supposed to clear. So the anchor moves to the extreme and the
 * label is positioned away from there, which makes "clear of the bar" true by construction rather
 * than true for most bars.
 *
 * <p>`bar` is a row of ECharts candlestick order — <code>[open, close, low, high]</code>, which is
 * NOT the order the name OHLC implies, and is the trap this repo has already documented twice.
 *
 * <p>With no bar — a date off the chart, or a caller that has none — it falls back to the fill.
 * A card at the fill price is slightly less well placed; a card at a guessed extreme would be in
 * the wrong place and look deliberate.
 */
export const anchorPriceFor = (side, bar, fillPrice) => {
  const extreme = Array.isArray(bar) ? (side === CARD_SIDE.BELOW ? bar[2] : bar[3]) : null;
  return Number.isFinite(extreme) ? extreme : fillPrice;
};

/* ── confinement ──────────────────────────────────────────────────────────────────────────── */

/** Breathing room between the card and the edge it is being held off. */
export const CONFINE_PADDING = 6;

/**
 * Pushes a label's rectangle back inside the plot area.
 *
 * <h3>Why this is a pure function taking a rect</h3>
 *
 * ECharts calls {@code labelLayout} with the label's measured {@code labelRect} and honours a
 * returned {@code dx}/{@code dy}. Doing the clamp here rather than inline in the option means the
 * rule can be tested without a canvas — and the rule is the whole requirement, so it is the part
 * that must not be assumed.
 *
 * <p>Clamped to the GRID rect, not the canvas. The requirement is that the card is never "obscured
 * by the axis margins", and the canvas includes them: a card held inside the canvas can still sit
 * on top of the price axis, which is worse than clipping because it looks deliberate.
 *
 * <p>When the card is larger than the area in a dimension it is pinned to the TOP-LEFT of that
 * dimension rather than centred. A card too tall to fit must lose its bottom, not its first line —
 * the first line is the action, which is the thing being read.
 */
export const confineLabelRect = (labelRect, bounds, padding = CONFINE_PADDING) => {
  if (!labelRect || !bounds) return { dx: 0, dy: 0 };
  const minX = bounds.left + padding;
  const maxX = bounds.right - padding;
  const minY = bounds.top + padding;
  const maxY = bounds.bottom - padding;

  let dx = 0;
  if (labelRect.width >= maxX - minX) {
    dx = minX - labelRect.x;
  } else if (labelRect.x < minX) {
    dx = minX - labelRect.x;
  } else if (labelRect.x + labelRect.width > maxX) {
    dx = maxX - (labelRect.x + labelRect.width);
  }

  let dy = 0;
  if (labelRect.height >= maxY - minY) {
    dy = minY - labelRect.y;
  } else if (labelRect.y < minY) {
    dy = minY - labelRect.y;
  } else if (labelRect.y + labelRect.height > maxY) {
    dy = maxY - (labelRect.y + labelRect.height);
  }

  return { dx, dy };
};

/**
 * The price grid's pixel rectangle.
 *
 * <p>DERIVED FROM THE SAME NUMBERS the grid is configured with, and the caller passes them in
 * rather than this module knowing them: two independent copies of "4% / 30px / 5%" is how a clamp
 * comes to hold a card inside a rectangle the plot no longer occupies, which looks like the clamp
 * not working at all.
 */
export const gridRect = ({ width, height, dividerPercent }) => ({
  left: width * 0.04,
  right: width - 30,
  top: height * 0.05,
  bottom: height * (0.05 + (dividerPercent - 2.5) / 100),
});

/* ── the card's appearance ────────────────────────────────────────────────────────────────── */

/**
 * TWO DARK SCHEMES, one per ACTION, with colour on the RETURN and nowhere else.
 *
 * <h3>Why the two differ, and why only slightly</h3>
 *
 * An entry is navy and an exit is charcoal. Both are dark, so the wall of cards still reads as one
 * kind of object — the previous black-on-white/white-on-black pairing was louder than the price
 * action underneath. The separation is a shift in HUE at nearly equal luminance, which the eye
 * sorts into two groups without any card shouting.
 *
 * <p>It is a SECOND encoding of the action, not the only one: the first line still says ENTRY or
 * EXIT in words, and the hemisphere says it a third time by position. That redundancy is
 * deliberate — colour alone fails for a colour-blind reader, and position alone fails on a card
 * that has been shifted to dodge a neighbour.
 *
 * <h3>Why the accent is on the value only</h3>
 *
 * A green-filled card is a retail brokerage. Tinting the whole box also makes the WIN cards
 * dominate a screenshot by area rather than by importance, and on a losing run turns the chart
 * into a red field that says nothing a reader did not already know. One coloured number per card
 * carries the same fact and costs no visual weight.
 */
export const DECORATOR_THEME = {
  /** Deep institutional navy. */
  entry: { background: '#162032', text: '#F5F5F5', border: '#2A3B5C', muted: '#8C9BB5' },
  /** Stark charcoal. */
  exit: { background: '#121212', text: '#F5F5F5', border: '#333333', muted: '#9A9A9A' },
};

/** The outcome accents. Muted enough to sit on near-black without glowing. */
export const PNL_COLORS = {
  win: '#2E8B57',
  loss: '#FF3B30',
  /** A scratch takes the body colour — it is not an outcome worth a hue. */
  flat: '#F5F5F5',
};

/**
 * Type sizing for a dense telemetry tag.
 *
 * <p>Ten pixels with a thirteen-pixel line and three pixels of vertical padding. The previous
 * card was 11/15/6 and read as a presentation slide: at 774 open cards the difference is most of
 * the vertical space on the chart.
 */
export const DECORATOR_TYPE = {
  fontSize: 10,
  lineHeight: 13,
  padding: [3, 5, 3, 5],
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

/**
 * THE WIDTH CAP, in pixels, on the ACTION line only.
 *
 * <p>The action line is the only one that can be long, because it carries the exit rule:
 * <code>EXIT (CONFORMING BAR TRAIL HIT)</code> is 31 characters, and at this monospaced 10px that
 * is about 186px of box. The date is always 10 characters and the price and return are shorter
 * still, so capping this one line caps the card — and a card's WIDTH is what makes it collide
 * horizontally with the next one along.
 *
 * <p>130 sits in the middle of the 120–140 the brief allows and wraps that worst case onto two
 * lines. It is a FIXED width in ECharts, not a maximum, so every card now has the same footprint
 * — which also makes the collision behaviour uniform instead of depending on the exit vocabulary.
 *
 * <h3>NEITHER `width` PLACEMENT WORKS, SO THE WRAP IS DONE HERE</h3>
 *
 * Both obvious implementations were tried on a real chart and both failed, for reasons visible in
 * zrender's own {@code parseText.js}:
 *
 * <ol>
 *   <li><b>`width` + `overflow` on the rich token</b> — the literal reading of "cap the long
 *       line". {@code parseRichText} builds its wrap state from the TOP-LEVEL style only
 *       ({@code var topWidth = style.width}), so a token width merely sizes that token's box and
 *       never wraps it. Measured: {@code EXIT (INITIAL STATIC STOP HIT)} rendered <b>171 × 60</b>
 *       — one unwrapped line, and WIDER than an uncapped card.</li>
 *   <li><b>`width` + `overflow` on the label</b> — this does wrap, and it destroys the card. With
 *       a {@code wrapInfo} in play, {@code pushTokens} sends an untyped token through
 *       {@code wrapText} instead of {@code str.split('\n')}, so the `\n` separators between the
 *       four lines stop forcing breaks and the tokens REFLOW. Measured: a four-line card rendered
 *       <b>2 lines</b> and the eight-line BOTH card rendered <b>4</b> — price and return packed
 *       onto one line because together they fit.</li>
 * </ol>
 *
 * So the wrap happens in the formatter, at a CHARACTER count, and the label carries no width at
 * all. That is exact rather than approximate because the face is monospaced — the stack ends in
 * `monospace`, so there is no environment where it is not. Measured with `measureText` at this
 * size: <b>5.498px per character in both weights</b>, so 130px is 23 characters.
 *
 * <p>The cap is then enforced by the content rather than by a constraint, which also means the
 * four lines survive: with no `wrapInfo`, every `\n` splits exactly as written.
 */
export const DECORATOR_WIDTH = 130;

/** The measured advance of one character at {@link DECORATOR_TYPE}'s size, in pixels. */
export const DECORATOR_CHAR_WIDTH = 5.498;

/** How many characters fit the cap. Floored, so the line is never the thing that exceeds it. */
export const DECORATOR_WRAP_CHARS = Math.floor(DECORATOR_WIDTH / DECORATOR_CHAR_WIDTH);

/**
 * Wraps one line to {@link DECORATOR_WRAP_CHARS}, at spaces where it can.
 *
 * <p>At SPACES rather than mid-character, because these are words a reader scans and
 * <code>TRAIL</code> split as <code>TRA/IL</code> is harder to read than a short line. A single
 * word longer than the limit is hard-broken anyway — there is nothing else to do with it, and
 * letting it through would reopen the sprawl this exists to close.
 */
export const wrapToChars = (text, limit = DECORATOR_WRAP_CHARS) => {
  const s = String(text == null ? '' : text);
  if (s.length <= limit) return [s];
  const out = [];
  let line = '';
  for (const word of s.split(' ')) {
    let w = word;
    // A word that cannot fit on a line of its own is broken until it can.
    while (w.length > limit) {
      if (line) { out.push(line); line = ''; }
      out.push(w.slice(0, limit));
      w = w.slice(limit);
    }
    if (!line) line = w;
    else if (line.length + 1 + w.length <= limit) line += ` ${w}`;
    else { out.push(line); line = w; }
  }
  if (line) out.push(line);
  return out;
};

/**
 * The per-line rich styles the formatter's tags refer to.
 *
 * <p>Built rather than written as a literal so the type scale above is the only place a size
 * lives — a rich style that set its own fontSize would silently opt out of it.
 *
 * <p>NO `width` or `overflow` here — see DECORATOR_WIDTH. Those belong on the label, which is the
 * only place zrender reads them from, and setting a token width as well would re-widen the box
 * the label-level cap has just constrained.
 */
export const decoratorRich = (half) => {
  const t = themeFor(half);
  const base = { fontSize: DECORATOR_TYPE.fontSize, lineHeight: DECORATOR_TYPE.lineHeight,
    fontFamily: DECORATOR_TYPE.fontFamily };
  return {
    a: { ...base, color: t.text, fontWeight: 600 },
    // The date is the least interesting line and is dimmed, so it stops competing with the two
    // numbers either side of it.
    d: { ...base, color: t.muted },
    p: { ...base, color: t.text },
    win: { ...base, color: PNL_COLORS.win, fontWeight: 600 },
    loss: { ...base, color: PNL_COLORS.loss, fontWeight: 600 },
    flat: { ...base, color: PNL_COLORS.flat },
  };
};

/**
 * The scheme for one half.
 *
 * <p>BOTH takes the ENTRY scheme, because that card is anchored at the entry coordinate and its
 * first line is the entry. It is the lesser of two wrong answers — the card genuinely describes
 * both ends — and it is at least consistent with where it sits and what it opens with.
 */
export const themeFor = (half) =>
  (half === TRADE_HALF.EXIT ? DECORATOR_THEME.exit : DECORATOR_THEME.entry);
